/**
 * Team, People: the directory (plan docs/plans/lms-plan.md 13 tab 6, 4.9,
 * 18.20; Appendix A GET /admin/directory; WP-32's route, WP-43's client;
 * WP-45).
 *
 * The people who can reach a site the acting admin reaches, nobody else (G14,
 * 4.9): name, email, platform role, the sites they reach (only the admin's),
 * and whether they are a Learning Center admin. A search box matches a name or
 * an email (typing waits a moment before asking); a site filter narrows to one
 * site of this network. A row opens the person page.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Contact } from 'lucide-react';
import EmptyState from '../../../../ui/EmptyState';
import FilterSelect from '../../../../ui/FilterSelect';
import SearchInput from '../../../../ui/SearchInput';
import { secondaryBtnCls } from '../../../../ui/styles';
import { useAuth } from '../../../../contexts/AuthContext';
import { listDirectory } from '../../../api/directory';
import { Pill } from '../../../components/StatusPill';
import {
  metaCls,
  personName,
  roleLabel,
  rowCls,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  theadCls,
  thCls,
} from '../../../components/pickers/shared';
import { LoadFailed, Loading, personHref, useAsync } from '../matrix/shared';

/** How long typing waits before the directory is asked (a search per keystroke would flood it). */
export const DIRECTORY_SEARCH_DEBOUNCE_MS = 250;
/** Rows per page. */
export const DIRECTORY_PAGE = 50;

export function Directory() {
  const navigate = useNavigate();
  const { availableSites, currentSite } = useAuth();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [siteId, setSiteId] = useState('');
  const [offset, setOffset] = useState(0);

  // Debounce the search box; clearing it asks at once.
  useEffect(() => {
    const text = search.trim();
    if (!text) {
      setQ('');
      setOffset(0);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setQ(text);
      setOffset(0);
    }, DIRECTORY_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const directory = useAsync(
    () => listDirectory({ q, siteId: siteId || null, limit: DIRECTORY_PAGE, offset }),
    JSON.stringify({ q, siteId, offset }),
    'The directory could not be loaded.'
  );

  const networkSites = useMemo(
    () =>
      (availableSites ?? [])
        .filter((s) => s.is_active !== false && (!currentSite?.network_id || s.network_id === currentSite.network_id))
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [availableSites, currentSite]
  );

  const items = directory.data?.items ?? [];
  const page = directory.data?.page;

  return (
    <div className="space-y-4" data-testid="team-directory">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name or email"
          label="Search people"
          className="w-full sm:w-72"
        />
        <FilterSelect
          label="Site"
          value={siteId}
          onChange={(v) => {
            setSiteId(v);
            setOffset(0);
          }}
          auto
        >
          <option value="">All my sites</option>
          {networkSites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </FilterSelect>
      </div>

      {directory.error ? <LoadFailed message={directory.error} onRetry={directory.reload} testId="directory-error" /> : null}
      {!directory.data && directory.loading ? <Loading rows={4} /> : null}

      {directory.data && items.length === 0 ? (
        <EmptyState
          icon={Contact}
          variant="card"
          title="Nobody found"
          hint={q ? 'Nobody you can reach matches this search.' : 'Nobody can reach the sites you administer yet.'}
        />
      ) : null}

      {items.length > 0 ? (
        <div className={tableCardCls}>
          <table className={tableCls}>
            <thead className={theadCls}>
              <tr>
                <th scope="col" className={thCls}>
                  Name
                </th>
                <th scope="col" className={thCls}>
                  Role
                </th>
                <th scope="col" className={thCls}>
                  Sites
                </th>
                <th scope="col" className={thCls}>
                  <span className="sr-only">Flags</span>
                </th>
              </tr>
            </thead>
            <tbody className={tbodyCls}>
              {items.map((p) => (
                <tr
                  key={p.user_id}
                  className={`${rowCls} cursor-pointer`}
                  onClick={() => navigate(personHref(p.user_id))}
                  data-testid={`directory-row-${p.user_id}`}
                >
                  <td className={tdCls}>
                    <button
                      type="button"
                      className="text-left font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(personHref(p.user_id));
                      }}
                      data-testid={`directory-open-${p.user_id}`}
                    >
                      {personName(p)}
                    </button>
                    {p.email && p.full_name ? <div className={metaCls}>{p.email}</div> : null}
                  </td>
                  <td className={tdCls}>{roleLabel(p.role)}</td>
                  <td className={tdCls}>{p.sites.map((s) => s.name ?? 'Unnamed site').join(', ') || '\u2014'}</td>
                  <td className={tdCls}>
                    <div className="flex flex-wrap gap-1.5">
                      {p.is_lms_admin ? (
                        <Pill tone="primary" dot={false}>
                          Learning Center admin
                        </Pill>
                      ) : null}
                      {!p.is_active ? (
                        <Pill tone="gray" dot={false}>
                          Deactivated
                        </Pill>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {page && (page.offset > 0 || page.hasMore) ? (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className={secondaryBtnCls}
            disabled={page.offset === 0 || directory.loading}
            onClick={() => setOffset(Math.max(0, page.offset - DIRECTORY_PAGE))}
          >
            Previous
          </button>
          <button
            type="button"
            className={secondaryBtnCls}
            disabled={!page.hasMore || directory.loading}
            onClick={() => setOffset(page.offset + DIRECTORY_PAGE)}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default Directory;
